import { VCREDIST_URL } from "@vortex/shared";
import { getErrorCode, getErrorMessageOrDefault, unknownToError, } from "@vortex/shared";
import PromiseBB from "bluebird";
import { spawn } from "child_process";
import { EventEmitter } from "events";
import * as fs from "fs-extra";
import * as fuzz from "fuzzball";
import JsonSocket from "json-socket";
import * as _ from "lodash";
import * as net from "net";
import * as path from "path";
import { toast } from "react-hot-toast";
import * as semver from "semver";
import { generate as shortid } from "shortid";
import stringFormat from "string-template";
import { fileMD5 } from "./util/checksum";
import { forgetExtension, setExtensionEnabled, setExtensionVersion, } from "./actions/app";
import { addNotification, closeDialog, dismissAllNotifications, dismissNotification, showDialog, } from "./actions/notifications";
import { suppressNotification } from "./actions/notificationSettings";
import { setExtensionLoadFailures } from "./actions/session";
import { setOptionalExtensions } from "./extensions/extension_manager/actions";
import { log } from "./logging";
import { registerSanityCheck } from "./store/reduxSanity";
import ReduxWatcher from "./store/ReduxWatcher";
import { computeStateDiff } from "./store/stateDiff";
import StyleManager from "./StyleManager";
import { getApplication } from "./util/application";
import { Archive } from "./util/archives";
import { COMPANY_ID } from "./util/constants";
import { MissingDependency, NotSupportedError, ProcessCanceled, ThirdPartyError, TimeoutError, UserCanceled, } from "./util/CustomErrors";
import { runElevated } from "./util/elevated";
import { disableErrorReport, isOutdated, recordErrorSpan, } from "./util/errorHandling";
import * as fsVortex from "./util/fs";
import getVortexPath from "./util/getVortexPath";
import { TString } from "./util/i18n";
import lazyRequire from "./util/lazyRequire";
import { showError } from "./util/message";
import { deregisterProtocolHandler, registerProtocolHandler, } from "./util/protocolRegistration";
import runElevatedCustomTool from "./util/runElevatedCustomTool";
import { activeGameId } from "./util/selectors";
import { getSafe } from "./util/storeHelper";
import { filteredEnvironment, isFunction, setdefault, timeout, truthy, wrapExtCBAsync, wrapExtCBSync, } from "./util/util";
import { webpackRequireHack } from "./util/webpack-hacks";
import { IPCDownloadAdapter } from "./IPCDownloadAdapter";
const modmeta = lazyRequire(() => require("modmeta-db"));
export function isExtSame(installed, remote) {
    if (installed.modId !== undefined) {
        return installed.modId === remote.modId;
    }
    return installed.name === remote.name;
}
const ERROR_OUTPUT_CUTOFF = 3;
// Dialog functions - now use window.api preload bridge
const showOpenDialog = (options) => {
    return window.api.dialog.showOpen(options);
};
const showSaveDialog = (options) => {
    return window.api.dialog.showSave(options);
};
const appExit = (exitCode) => {
    return window.api.app.exit(exitCode);
};
const showErrorBox = (title, content) => {
    return window.api.dialog.showErrorBox(title, content);
};
const showMessageBox = (options) => {
    return window.api.dialog.showMessageBox(options);
};
function applyVariables(arg, variables) {
    return stringFormat(arg, variables);
}
class ExtEventHandler extends EventEmitter {
    mWrappee;
    mExtension;
    mFuncMap = new Map();
    constructor(wrappee, extension) {
        super();
        this.mWrappee = wrappee;
        this.mExtension = extension;
    }
    addListener(event, listener) {
        this.mWrappee.addListener(event, this.makeWrapped(event, listener));
        return this;
    }
    on(event, listener) {
        const stack = new Error().stack;
        return this.addListener(event, listener);
    }
    once(event, listener) {
        this.mWrappee.once(event, this.makeOnceWrapped(event, listener));
        return this;
    }
    prependListener(event, listener) {
        this.mWrappee.prependListener(event, this.makeWrapped(event, listener));
        return this;
    }
    prependOnceListener(event, listener) {
        this.mWrappee.prependOnceListener(event, this.makeOnceWrapped(event, listener));
        return this;
    }
    removeListener(event, listener) {
        if (this.mFuncMap.has(event)) {
            const listeners = this.mFuncMap.get(event);
            const idx = listeners.findIndex((iter) => iter.orig === listener);
            if (idx !== -1) {
                this.mWrappee.removeListener(event, listeners[idx].wrapped);
                listeners.splice(idx, 1);
            }
        }
        return this;
    }
    off(event, listener) {
        return this.removeListener(event, listener);
    }
    removeAllListeners(event) {
        this.mWrappee.removeAllListeners(event);
        return this;
    }
    setMaxListeners(n) {
        this.mWrappee.setMaxListeners(n);
        return this;
    }
    getMaxListeners() {
        return this.mWrappee.getMaxListeners();
    }
    // tslint:disable-next-line:ban-types
    listeners(event) {
        return this.mWrappee.listeners(event);
    }
    // tslint:disable-next-line:ban-types
    rawListeners(event) {
        return this.mWrappee.rawListeners(event);
    }
    emit(event, ...args) {
        return this.mWrappee.emit(event, ...args);
    }
    eventNames() {
        return this.mWrappee.eventNames();
    }
    listenerCount(type) {
        return this.mWrappee.listenerCount(type);
    }
    funcMap(event) {
        if (!this.mFuncMap.has(event)) {
            this.mFuncMap.set(event, []);
        }
        return this.mFuncMap.get(event);
    }
    makeWrapped(event, listener) {
        const wrapped = wrapExtCBSync(listener, convertExtInfo(this.mExtension));
        this.funcMap(event).push({ orig: listener, wrapped });
        return wrapped;
    }
    makeOnceWrapped(event, listener) {
        const wrapped = wrapExtCBSync((...args) => {
            listener(...args);
            this.removeListener(event, listener);
        }, convertExtInfo(this.mExtension));
        this.funcMap(event).push({ orig: listener, wrapped });
        return wrapped;
    }
}
function convertExtInfo(ext) {
    if (ext === undefined) {
        return undefined;
    }
    return {
        name: ext.info?.name ?? ext.name,
        namespace: ext.namespace,
        path: ext.path,
        dynamic: ext.dynamic,
        official: ext.info?.bundled ?? true,
    };
}
class APIProxyHandler {
    mExtension;
    mEnabled;
    mEvents;
    constructor(extension, enable, events) {
        this.mExtension = extension;
        this.mEnabled = enable;
        this.mEvents = new ExtEventHandler(events, this.mExtension);
    }
    enable() {
        this.mEnabled = true;
    }
    get(target, key) {
        if (key === "extension") {
            return this.mExtension;
        }
        else if (key === "translate") {
            return target[key];
        }
        else if (key === "onAsync") {
            return (eventName, listener) => target["onAsync"](eventName, listener, convertExtInfo(this.mExtension));
        }
        else if (key === "onStateChange") {
            return (statePath, callback) => target[key](statePath, callback, this.mExtension);
        }
        else if (key === "events") {
            return this.mEvents;
        }
        else if (key === "laterT") {
            return (input, options) => new TString(input, options, this.mExtension.namespace);
        }
        else if (key === "NAMESPACE") {
            return this.mExtension.namespace;
        }
        if (!this.mEnabled) {
            throw new Error("extension uses api in init function");
        }
        return target[key];
    }
}
class APIProxyCreator {
    mExtension;
    mProxyHandler;
    mProxy;
    mEvents;
    mAPIEnabled = false;
    constructor(extension, events) {
        this.mExtension = extension;
        this.mEvents = events;
    }
    enableAPI() {
        this.mAPIEnabled = true;
        if (this.mProxyHandler !== undefined) {
            this.mProxyHandler.enable();
        }
    }
    get(target, key) {
        if (key === "api") {
            if (this.mProxy === undefined) {
                this.mProxyHandler = new APIProxyHandler(this.mExtension, this.mAPIEnabled, this.mEvents);
                this.mProxy = new Proxy(target[key], this.mProxyHandler);
            }
            return this.mProxy;
        }
        else {
            return target[key];
        }
    }
}
class ContextProxyHandler {
    mContext;
    mInitCalls;
    mApiAdditions;
    mCurrentExtension;
    mCurrentPath;
    mOptional;
    mMayRegister = true;
    constructor(context) {
        this.mContext = context;
        this.mInitCalls = [];
        this.mApiAdditions = [];
        // TODO: check if this is necessary. Ususally the arrow lambda should
        //   bind this automatically
        // tslint:disable-next-line:no-this-assignment
        const that = this;
        this.mOptional = new Proxy({}, {
            get(target, key) {
                return (...args) => {
                    if (!that.mMayRegister) {
                        log("warn", "extension tries to use register call outside init function", {
                            extension: that.mCurrentExtension,
                            call: key,
                        });
                        return;
                    }
                    that.mInitCalls.push({
                        extension: that.mCurrentExtension,
                        extensionPath: that.mCurrentPath,
                        key: key.toString(),
                        arguments: args,
                        optional: true,
                    });
                };
            },
        });
    }
    endRegistration() {
        this.mMayRegister = false;
    }
    /**
     * returns the parameters of calls to the specified function
     */
    getCalls(name) {
        return this.mInitCalls.filter((call) => call.key === name);
    }
    dropCalls(extNames) {
        this.mInitCalls = this.mInitCalls.filter((iter) => iter.extension !== extNames);
    }
    invokeAdditions(extensions) {
        this.mApiAdditions.forEach((addition) => {
            this.getCalls(addition.key).forEach((call) => {
                const ext = extensions.find((iter) => iter.name === call.extension);
                const extInfo = convertExtInfo(ext);
                addition.callback(...call.arguments, call.extensionPath, extInfo);
            });
        });
    }
    /**
     * Retrieve the map of optional extensions
     *  Each optional requireExtension call is added against the id of the extension that requires it.
     */
    getOptionalExtensions(allExtensions) {
        const optionalRequireCalls = this.getCalls("requireExtension").filter((iter) => iter.arguments.length > 2 && iter.arguments[2] === true);
        const missingOptionals = optionalRequireCalls.reduce((acc, iter) => {
            const callingExtensionKey = iter.extension;
            const requiredKey = iter.arguments[0];
            const ext = this.findExt(requiredKey, allExtensions);
            if (ext === undefined) {
                const optional = {
                    id: requiredKey,
                    args: iter.arguments,
                    extensionPath: iter.extensionPath,
                };
                acc = {
                    ...acc,
                    [callingExtensionKey]: [].concat(acc[callingExtensionKey] || [], optional),
                };
            }
            return acc;
        }, {});
        return missingOptionals;
    }
    /**
     * remove all init calls from incompatible extensions
     */
    unloadIncompatible(furtherAPIs, allExtensions) {
        const addAPIs = this.mApiAdditions.map((addition) => addition.key);
        const fullAPI = new Set([...furtherAPIs, ...this.staticAPIs, ...addAPIs]);
        const incompatibleExtensions = {};
        this.mInitCalls
            .filter((call) => !call.optional && !fullAPI.has(call.key))
            .forEach((call) => {
            log("debug", "unsupported api call", {
                extension: call.extension,
                api: call.key,
            });
            setdefault(incompatibleExtensions, call.extension, []).push({
                id: "unsupported-api",
            });
        });
        const testValid = (extId, requiredId, version, optional) => {
            if (!optional) {
                const req = this.findExt(requiredId, allExtensions);
                if (req === undefined) {
                    setdefault(incompatibleExtensions, extId, []).push({
                        id: "dependency",
                        args: { dependencyId: requiredId },
                    });
                }
                else if (version !== undefined &&
                    !semver.satisfies(req.info?.version, version)) {
                    setdefault(incompatibleExtensions, extId, []).push({
                        id: "dependency",
                        args: { dependencyId: requiredId, version },
                    });
                }
            }
        };
        this.getCalls("requireExtension").forEach((call) => {
            testValid(call.extension, ...call.arguments);
        });
        this.getCalls("requireVersion").forEach((call) => {
            if (process.env.NODE_ENV !== "development" &&
                !semver.satisfies(getApplication().version, call.arguments[0], {
                    includePrerelease: true,
                })) {
                setdefault(incompatibleExtensions, call.extension, []).push({
                    id: "unsupported-version",
                });
            }
        });
        if (Object.keys(incompatibleExtensions).length > 0) {
            log("info", "extensions ignored for using unsupported api", {
                extensions: Object.keys(incompatibleExtensions).join(", "),
            });
            this.mInitCalls = this.mInitCalls.filter((call) => incompatibleExtensions[call.extension] === undefined);
        }
        else {
            log("debug", "all extensions compatible");
        }
        return incompatibleExtensions;
    }
    /**
     * change the extension name currently being loaded
     */
    setExtension(extension, extensionPath) {
        this.mCurrentExtension = extension;
        this.mCurrentPath = extensionPath;
    }
    has(target, key) {
        return true;
    }
    get(target, key) {
        if (key in this.mContext) {
            return this.mContext[key];
        }
        else if (key === "optional") {
            return this.mOptional;
        }
        return key in this.mContext
            ? this.mContext[key]
            : (...args) => {
                if (!this.mMayRegister) {
                    log("warn", "extension tries to use register call outside init function", {
                        extension: this.mCurrentExtension,
                        call: key,
                    });
                    return;
                }
                this.mInitCalls.push({
                    extension: this.mCurrentExtension,
                    extensionPath: this.mCurrentPath,
                    key: key.toString(),
                    arguments: args,
                    optional: false,
                });
            };
    }
    set(target, key, value, receiver) {
        this.mApiAdditions.push({
            key: key.toString(),
            callback: value,
        });
        return true;
    }
    get staticAPIs() {
        // trick so we get a compile time error from tsc if this object doesn't
        // match the interface
        const dummy = {
            registerMainPage: undefined,
            registerDashlet: undefined,
            registerDialog: undefined,
            registerOverlay: undefined,
            registerSettings: undefined,
            registerAction: undefined,
            registerControlWrapper: undefined,
            registerBanner: undefined,
            registerDeploymentMethod: undefined,
            registerInstaller: undefined,
            registerFooter: undefined,
            registerToDo: undefined,
            registerModSource: undefined,
            registerReducer: undefined,
            registerPersistor: undefined,
            registerSettingsHive: undefined,
            registerTableAttribute: undefined,
            registerTest: undefined,
            registerArchiveType: undefined,
            registerGame: undefined,
            registerGameStub: undefined,
            registerGameStore: undefined,
            registerGameInfoProvider: undefined,
            registerAttributeExtractor: undefined,
            registerModType: undefined,
            registerActionCheck: undefined,
            registerMerge: undefined,
            registerInterpreter: undefined,
            registerStartHook: undefined,
            registerMigration: undefined,
            registerToolVariables: undefined,
            registerLoadOrderPage: undefined,
            registerLoadOrder: undefined,
            registerGameSpecificCollectionsData: undefined,
            registerHistoryStack: undefined,
            registerDownloadProtocol: undefined,
            registerAPI: undefined,
            requireVersion: undefined,
            requireExtension: undefined,
            api: undefined,
            once: undefined,
            onceMain: undefined,
            optional: undefined,
        };
        return Object.keys(dummy);
    }
    findExt = (id, allExtensions) => {
        return allExtensions.find((ext) => ext.info?.name === id || ext.info?.id === id || ext.name === id);
    };
}
const UNDEFINED = {};
function convertMD5Result(input) {
    return input;
}
/**
 * interface to extensions. This loads extensions and provides the api extensions
 * use
 *
 * @class ExtensionManager
 */
class ExtensionManager {
    static registerUIAPI(name) {
        ExtensionManager.sUIAPIs.add(name);
    }
    static getExtensionPaths() {
        // only the first extension with a specific name is loaded, so
        // load the bundled ones last so a user can replace them
        return [
            { path: path.join(getVortexPath("userData"), "plugins"), bundled: false },
            { path: getVortexPath("bundledPlugins"), bundled: true },
        ];
    }
    static sUIAPIs = new Set();
    mExtensions;
    mApi;
    mTranslator;
    mEventEmitter;
    mStyleManager;
    mReduxWatcher;
    mWatches = {};
    mProtocolHandlers = {};
    mRepositoryLookup = {};
    mArchiveHandlers;
    mModDB;
    mModDBPromise;
    mModDBGame;
    mModDBAPIKey;
    mModDBCache = {};
    mContextProxyHandler;
    mDownloadAdapter;
    mExtensionState;
    mLoadFailures = {};
    mOptionalExtensions = {};
    mInterpreters;
    mStartHooks;
    mToolParameterCBs;
    mLoadingCallbacks = [];
    mProgrammaticMetaServers = {};
    mForceDBReconnect = false;
    mOnUIStarted;
    mUIStartedPromise;
    mOutdated = [];
    mFailedWatchers = new Set();
    mExtensionFormats = ["index.cjs", "index.js"];
    // Pending actions to dispatch when setStore() is called (renderer-only architecture)
    mPendingDisables = [];
    mPendingRemoves = [];
    // Extension-registered persistors for custom hives (e.g., loadOrder -> plugins.txt)
    mExtensionPersistors = {};
    // Previous state for extension persistor diff computation
    mPersistorPrevState = {};
    // Debounce timers for extension persistors
    mPersistorTimers = {};
    /**
     * Create ExtensionManager.
     *
     * In the new renderer-only architecture:
     * - Pass extensionState directly (from hydrated app.extensions)
     * - Store will be set later via setStore() after reducer is initialized
     *
     * @param extensionState - Pre-loaded extension state from hydration (renderer-only)
     * @param eventEmitter - Event emitter for extension communication
     */
    constructor(extensionState, eventEmitter) {
        this.mEventEmitter = eventEmitter;
        if (eventEmitter !== undefined) {
            this.mEventEmitter.setMaxListeners(100);
        }
        this.mUIStartedPromise = new PromiseBB((resolve) => {
            this.mOnUIStarted = resolve;
        });
        this.mInterpreters = {};
        this.mStartHooks = [];
        this.mToolParameterCBs = [];
        this.mApi = {
            showErrorNotification: this.showErrorBox,
            selectFile: this.selectFile,
            saveFile: this.saveFile,
            selectExecutable: this.selectExecutable,
            selectDir: this.selectDir,
            events: this.mEventEmitter,
            translate: (input, options) => {
                if (this.mTranslator == null) {
                    return Array.isArray(input) ? input[0].toString() : input.toString();
                }
                if (options == null) {
                    options = {};
                }
                // This is a namespace key, use new separators.
                if (options?.isNamespaceKey) {
                    return this.mTranslator.t(input, {
                        ...options,
                        keySeparator: ".",
                        nsSeparator: ":",
                    });
                }
                // Not a namespace key, use old separators.
                return this.mTranslator.t(input, options);
            },
            laterT: (input, options) => new TString(input, options, "common"),
            locale: () => this.mTranslator.language,
            getI18n: () => this.mTranslator,
            getPath: this.getPath,
            onStateChange: (statePath, callback) => undefined,
            registerProtocol: this.registerProtocol,
            registerRepositoryLookup: this.registerRepositoryLookup,
            deregisterProtocol: this.deregisterProtocol,
            lookupModReference: this.lookupModReference,
            lookupModMeta: this.lookupModMeta,
            saveModMeta: this.saveModMeta,
            openArchive: this.openArchive,
            genMd5Hash: this.genMd5Hash,
            clearStylesheet: () => {
                /** NOTE(erri120): no-op */
            },
            setStylesheet: (key, filePath) => this.mStyleManager.addStylesheet(key, filePath),
            runExecutable: this.runExecutable,
            emitAndAwait: this.emitAndAwait,
            withPrePost: this.withPrePost,
            isOutdated: () => isOutdated(),
            onAsync: this.onAsync,
            highlightControl: this.highlightControl,
            addMetaServer: this.addMetaServer,
            getLoadedExtensions: () => this.extensions,
            awaitUI: () => this.mUIStartedPromise,
            getState: () => undefined,
            ext: {},
            NAMESPACE: "common",
        };
        this.mDownloadAdapter = new IPCDownloadAdapter(this.mApi);
        // Use provided extension state directly (renderer-only architecture)
        // Extensions that need to be removed will be handled when setStore() is called
        this.mExtensionState = extensionState ?? {};
        // Handle extensions that caused crashes - mark for disabling
        // The actual dispatch will happen when setStore() is called
        this.mPendingDisables = [];
        try {
            const disableExtensions = fs
                .readdirSync(getVortexPath("temp"))
                .filter((name) => name.startsWith("__disable_"));
            disableExtensions.forEach((ext) => {
                const extId = ext.substr(10);
                log("info", "disabling extension that caused a crash before", {
                    extId,
                });
                this.mPendingDisables.push(extId);
                fs.unlinkSync(path.join(getVortexPath("temp"), ext));
            });
        }
        catch (err) {
            const code = getErrorCode(err);
            // an ENOENT will happen on the first start where the dir doesn't
            // exist yet. No problem
            if (code !== "ENOENT") {
                log("error", "failed to read disabled extensions", err);
            }
        }
        // Check for extensions marked for removal
        const extensionsPath = path.join(getVortexPath("userData"), "plugins");
        this.mPendingRemoves = [];
        Object.keys(this.mExtensionState)
            .filter((extId) => this.mExtensionState[extId].remove)
            .forEach((extId) => {
            const extPath = path.join(extensionsPath, extId);
            log("info", "removing", extPath);
            try {
                fs.removeSync(extPath);
                this.mPendingRemoves.push(extId);
            }
            catch (err) {
                // On Windows the previous renderer process may not have fully released
                // file handles yet despite waitForRendererExit in the main process.
                // Log and continue — the remove flag stays in state so it retries on
                // next startup.
                log("warn", "failed to remove extension, will retry on next startup", { extId, error: getErrorMessageOrDefault(err) });
            }
        });
        // Dynamic require to prevent TypeScript from analyzing StyleManager during api build
        this.mStyleManager = new StyleManager();
        this.mExtensions = this.prepareExtensions();
        log("info", "outdated extensions", { numOutdated: this.mOutdated.length });
        if (this.mOutdated.length > 0) {
            this.mOutdated.forEach((ext) => {
                log("info", "extension older than bundled version, will be removed", {
                    name: ext,
                });
                // Store pending removal - will be dispatched when setStore() is called
                this.mPendingRemoves.push(ext);
            });
            return;
        }
        this.initExtensions();
    }
    get hasOutdatedExtensions() {
        return this.mOutdated.length > 0;
    }
    setTranslation(translator) {
        this.mTranslator = translator;
    }
    get extensions() {
        return this.mExtensions;
    }
    /**
     * sets up the extension manager to work with the specified store
     *
     * @template S State interface
     * @param {Redux.Store<S>} store
     *
     * @memberOf ExtensionManager
     */
    setStore(store) {
        this.mReduxWatcher = new ReduxWatcher(store, this.watcherError);
        // Process any pending actions that were deferred during construction
        // (renderer-only architecture - dispatches happen after store is ready)
        this.mPendingDisables.forEach((extId) => {
            store.dispatch(setExtensionEnabled(extId, false));
        });
        this.mPendingDisables = [];
        this.mPendingRemoves.forEach((extId) => {
            store.dispatch(forgetExtension(extId));
        });
        this.mPendingRemoves = [];
        this.mExtensionState = getSafe(store.getState(), ["app", "extensions"], {});
        // Initialize extension-registered persistors (e.g., loadOrder -> plugins.txt)
        this.initExtensionPersistors(store);
        this.mApi.sendNotification = (notification) => {
            const noti = { ...notification };
            if (noti.id === undefined) {
                noti.id = shortid();
            }
            if (this.canBeToast(noti)) {
                try {
                    const toastFunc = noti.type === "error" ? toast.error : toast.success;
                    const toastOptions = {
                        id: noti.id,
                        duration: noti.displayMS,
                    };
                    const message = noti.title !== undefined
                        ? `${noti.title}:\n${noti.message ?? ""}`
                        : (noti.message ?? "");
                    if (message.length > 0) {
                        toastFunc(message, toastOptions);
                        return noti.id;
                    }
                }
                catch (err) {
                    // Toast rendering failed (e.g., goober styling error during race condition)
                    // Fall through to standard notification
                    log("warn", "Failed to show toast notification", err);
                }
            }
            if (notification.type === "warning") {
                log("warn", "warning notification", {
                    message: notification.message,
                    title: notification.title,
                });
            }
            else if (notification.type === "error") {
                log("warn", "error notification", {
                    message: notification.message,
                    title: notification.title,
                });
            }
            store.dispatch(addNotification(noti));
            return noti.id;
        };
        // tslint:disable-next-line:only-arrow-functions
        this.mApi.showErrorNotification = function (message, details, options) {
            let extension = this.extension;
            if (extension === undefined && details?.["extension"] !== undefined) {
                extension = this.getLoadedExtensions().find((iter) => iter.name === details["extension"]);
            }
            if (extension !== undefined &&
                extension.info !== undefined &&
                extension.info.author !== COMPANY_ID) {
                if (options === undefined) {
                    options = {};
                }
                if (options.allowReport !== false) {
                    options.extensionName = extension.info.name;
                    const remoteExtensions = this.getState().session
                        .extensions.available;
                    options.extensionRemote = remoteExtensions.find((ext) => isExtSame(extension.info, ext));
                }
                options.extension = extension;
            }
            showError(store.dispatch, message, details, options);
        };
        this.mApi.showDialog = (type, title, content, actions, id) => store.dispatch(showDialog(type, title, content, actions, id));
        this.mApi.closeDialog = (id, actionKey, input) => store.dispatch(closeDialog(id, actionKey, input));
        this.mApi.dismissNotification = (id) => store.dispatch(dismissNotification(id));
        this.mApi.dismissAllNotifications = () => store.dispatch(dismissAllNotifications());
        this.mApi.suppressNotification = (id, suppress) => {
            if (suppress !== false) {
                store.dispatch(dismissNotification(id));
            }
            store.dispatch(suppressNotification(id, suppress !== false));
        };
        this.mApi.store = store;
        this.mApi.getState = () => this.mApi.store.getState();
        this.mApi.onStateChange = this.stateChangeHandler;
        this.mDownloadAdapter.processInterruptedDownloads();
        this.mApi.onStateChange(["settings", "metaserver", "servers"], () => {
            this.mForceDBReconnect = true;
        });
        store.dispatch(setOptionalExtensions(this.mOptionalExtensions));
        store.dispatch(setExtensionLoadFailures(this.mLoadFailures));
        this.migrateExtensions();
        this.reportExtLoadErrors();
    }
    reportExtLoadErrors() {
        const nodeLoadErr = Object.values(this.mLoadFailures)
            .flat(1)
            .find((_) => {
            const msg = _.args?.message ?? "";
            return (msg.includes("The specified module could not be found.") &&
                msg.includes(".node"));
        });
        if (nodeLoadErr !== undefined) {
            this.mApi.store?.dispatch?.(showDialog("error", "Extension failed to load", {
                bbcode: "An unexpected error occurred while Vortex was loading extension:<br/><br/>{{message}}<br/><br/>" +
                    "This is often caused by a bad installation of the app, " +
                    "a security app interfering with Vortex " +
                    "or a problem with the Microsoft Visual C++ Redistributable installed on your PC. " +
                    "To solve this issue please try the following:<br/><br/>" +
                    "- Wait a moment and try starting Vortex again<br/>" +
                    "- Reinstall Vortex from the Nexus Mods website<br/>" +
                    "- Install the latest Microsoft Visual C++ Redistributable ([url]{{url}}[/url])<br/>" +
                    "- Disable anti-virus or other security apps that might interfere and install Vortex again<br/><br/>" +
                    "If the issue persists, please create a thread in our support forum for further assistance.",
                parameters: {
                    message: nodeLoadErr.args.message,
                    url: VCREDIST_URL,
                },
            }, [
                {
                    label: "Ignore",
                    action: () => disableErrorReport(),
                },
                {
                    label: "Close Vortex",
                    action: () => appExit(),
                },
            ], "ext-load-native-failed"));
        }
    }
    /**
     * gain acces to the extension api
     *
     * @returns
     *
     * @memberOf ExtensionManager
     */
    getApi() {
        return this.mApi;
    }
    /**
     * retrieve list of all reducers registered by extensions
     */
    getReducers() {
        const reducers = [];
        this.apply("registerReducer", (statePath, reducer) => {
            reducers.push({ path: statePath, reducer });
        });
        this.apply("registerActionCheck", (actionType, check) => {
            registerSanityCheck(actionType, check);
        });
        this.apply("registerInterpreter", (extension, apply) => {
            this.mInterpreters[extension.toLowerCase()] = apply;
        });
        this.apply("registerStartHook", (priority, id, hook) => {
            this.mStartHooks.push({ priority, id, hook });
        });
        this.apply("registerToolVariables", (func) => {
            this.mToolParameterCBs.push(func);
        });
        this.mStartHooks.sort((lhs, rhs) => lhs.priority - rhs.priority);
        return reducers;
    }
    /**
     * apply all extensions that were registered by extensions
     *
     * @memberOf ExtensionManager
     */
    applyExtensionsOfExtensions() {
        this.mContextProxyHandler.invokeAdditions(this.mExtensions);
        this.mContextProxyHandler
            .getCalls("registerDownloadProtocol")
            .forEach((call) => {
            const [scheme, handler] = call.arguments;
            this.mDownloadAdapter.registerProtocol(scheme, handler);
        });
    }
    /**
     * Initialize extension-registered persistors.
     * Should be called after the store is set up.
     * This enables extensions to persist custom state hives to external files
     * (e.g., loadOrder -> plugins.txt, userlist -> userlist.yaml)
     */
    initExtensionPersistors(store) {
        // Collect all persistor registrations from extensions
        this.apply("registerPersistor", (hive, persistor, debounce) => {
            log("info", "Registering extension persistor", { hive });
            this.mExtensionPersistors[hive] = {
                persistor,
                debounce: debounce ?? 200,
            };
            // Initialize previous state for this hive
            this.mPersistorPrevState[hive] = store.getState()[hive];
            // Set up the reset callback - called when persistor loads data from file
            // This hydrates the Redux state from the persistor's data
            persistor.setResetCallback(() => {
                return this.hydrateFromPersistor(hive, persistor, store);
            });
        });
        // Subscribe to store changes to notify extension persistors
        if (Object.keys(this.mExtensionPersistors).length > 0) {
            log("info", "Setting up extension persistor subscriptions", {
                hives: Object.keys(this.mExtensionPersistors),
            });
            store.subscribe(() => {
                const newState = store.getState();
                this.notifyExtensionPersistors(newState);
            });
        }
    }
    /**
     * Hydrate Redux state from a persistor's data.
     * Called when a persistor loads data from its backing store (e.g., plugins.txt).
     */
    async hydrateFromPersistor(hive, persistor, store) {
        try {
            log("debug", "Hydrating from extension persistor", { hive });
            // Build the hydration data from the persistor
            const hydrationData = {};
            if (persistor.getAllKVs !== undefined) {
                // Fast path: get all key-value pairs at once
                const kvPairs = await persistor.getAllKVs();
                for (const { key, value } of kvPairs) {
                    this.insertAtPath(hydrationData, key, this.deserialize(value));
                }
            }
            else {
                // Slow path: get keys first, then values
                const keys = await persistor.getAllKeys();
                for (const key of keys) {
                    try {
                        const value = await persistor.getItem(key);
                        this.insertAtPath(hydrationData, key, this.deserialize(value));
                    }
                    catch (err) {
                        log("warn", "Failed to get persistor item", {
                            hive,
                            key: key.join("."),
                            error: getErrorMessageOrDefault(err),
                        });
                    }
                }
            }
            // Dispatch hydration action to update Redux state
            store.dispatch({
                type: "__hydrate",
                payload: { [hive]: hydrationData },
            });
            // Update our tracked previous state to match what we just hydrated
            this.mPersistorPrevState[hive] = hydrationData;
            log("debug", "Extension persistor hydration complete", {
                hive,
                keyCount: Object.keys(hydrationData).length,
            });
        }
        catch (err) {
            recordErrorSpan("Failed to hydrate from extension persistor", unknownToError(err));
            log("error", "Failed to hydrate from extension persistor", {
                hive,
                error: getErrorMessageOrDefault(err),
            });
        }
    }
    /**
     * Insert a value at a nested path in an object.
     */
    insertAtPath(target, path, value) {
        let current = target;
        for (let i = 0; i < path.length - 1; i++) {
            if (current[path[i]] === undefined) {
                current[path[i]] = {};
            }
            current = current[path[i]];
        }
        if (path.length > 0) {
            current[path[path.length - 1]] = value;
        }
    }
    /**
     * Deserialize a value from the persistor.
     */
    deserialize(value) {
        if (value === undefined || value.length === 0) {
            return "";
        }
        try {
            return JSON.parse(value);
        }
        catch {
            return undefined;
        }
    }
    /**
     * Notify extension persistors of state changes.
     * Computes diffs and calls setItem/removeItem on each persistor.
     */
    notifyExtensionPersistors(newState) {
        for (const [hive, { persistor, debounce }] of Object.entries(this.mExtensionPersistors)) {
            const newHive = newState[hive];
            // Skip if hive hasn't changed from the last PERSISTED state
            if (this.mPersistorPrevState[hive] === newHive) {
                continue;
            }
            // Clear any pending timer for this hive
            if (this.mPersistorTimers[hive] !== undefined) {
                clearTimeout(this.mPersistorTimers[hive]);
            }
            // Schedule debounced persist - capture newHive but use stored prevState when running
            // This ensures multiple rapid changes are accumulated into one diff
            const capturedNewHive = newHive;
            this.mPersistorTimers[hive] = setTimeout(() => {
                const oldHive = this.mPersistorPrevState[hive];
                this.persistHiveChanges(hive, persistor, oldHive, capturedNewHive);
                // Update previous state AFTER successful persist
                this.mPersistorPrevState[hive] = capturedNewHive;
                delete this.mPersistorTimers[hive];
            }, debounce);
        }
    }
    /**
     * Persist changes for a specific hive by computing diff and calling persistor methods.
     */
    persistHiveChanges(hive, persistor, oldHive, newHive) {
        try {
            const operations = computeStateDiff(oldHive, newHive);
            for (const op of operations) {
                if (op.type === "set") {
                    Promise.resolve(persistor.setItem(op.path, JSON.stringify(op.value))).catch((err) => {
                        log("error", "Extension persistor setItem failed", {
                            hive,
                            path: op.path.join("."),
                            error: getErrorMessageOrDefault(err),
                        });
                    });
                }
                else {
                    Promise.resolve(persistor.removeItem(op.path)).catch((err) => {
                        log("error", "Extension persistor removeItem failed", {
                            hive,
                            path: op.path.join("."),
                            error: getErrorMessageOrDefault(err),
                        });
                    });
                }
            }
        }
        catch (err) {
            log("error", "Failed to compute extension persistor diff", {
                hive,
                error: getErrorMessageOrDefault(err),
            });
        }
    }
    /**
     * runs the extension init function with the specified register-function
     * set
     *
     * @param {string} funcName
     * @param {Function} func
     *
     * @memberOf ExtensionManager
     */
    apply(funcName, func, addExtInfo) {
        this.mContextProxyHandler.getCalls(funcName).forEach((call) => {
            try {
                if (addExtInfo === true) {
                    const ext = this.mExtensions.find((iter) => iter.name === call.extension);
                    const extInfo = _.pick(ext, ["name", "namespace", "path"]);
                    func(extInfo, ...call.arguments);
                }
                else {
                    func(...call.arguments);
                }
            }
            catch (unknownError) {
                const err = unknownToError(unknownError);
                this.mApi.showErrorNotification("Extension failed to initialize. If this isn't an official extension, " +
                    "please report the error to the respective author.", {
                    extension: call.extension,
                    err: err.message,
                    stack: err.stack,
                });
            }
        });
    }
    /**
     * call the "once" function for all extensions. This should really only be called
     * once.
     */
    doOnce() {
        // Since ExtensionManager is renderer-only, we need to handle both once and onceMain
        const onceCalls = this.mContextProxyHandler.getCalls("once");
        const onceMainCalls = this.mContextProxyHandler.getCalls("onceMain");
        const allCalls = [...onceCalls, ...onceMainCalls];
        const reportError = (err, call, allowReport = true) => {
            if (err instanceof Error) {
                log("warn", "failed to call once", {
                    err: err.message,
                    stack: err.stack,
                });
                recordErrorSpan("Extension once-callback failed", err, {
                    "extension.name": call.extension,
                });
            }
            else {
                log("warn", "failed to call once", err);
            }
            err["extension"] = call.extension;
            this.mApi.showErrorNotification("Extension failed to initialize. If this isn't an official extension, " +
                "please report the error to the respective author.", err, { allowReport });
        };
        return PromiseBB.mapSeries(allCalls, (call, idx) => {
            const isMainCall = onceMainCalls.includes(call);
            log("debug", isMainCall ? "onceMain" : "once", {
                extension: call.extension,
            });
            if (isMainCall) {
                log("warn", "onceMain is deprecated and won't work as expected");
            }
            const ext = this.mExtensions.find((iter) => iter.name === call.extension);
            this.mContextProxyHandler.setExtension(ext.name, ext.path);
            try {
                this.mLoadingCallbacks.forEach((cb) => {
                    cb(call.extension, idx);
                });
                let prom;
                prom = call.arguments[0]() || PromiseBB.resolve();
                const start = Date.now();
                return timeout(prom, 60000, {
                    throw: true,
                    queryContinue: () => this.queryLoadTimeout(call.extension),
                })
                    .then(() => {
                    const elapsed = Date.now() - start;
                    if (elapsed > 1000) {
                        log("debug", "slow initialization", {
                            extension: call.extension,
                            elapsed,
                        });
                    }
                })
                    .catch(TimeoutError, () => {
                    reportError(new Error("Initialization didn't finish in time."), call, false);
                })
                    .catch((err) => {
                    reportError(err, call);
                });
            }
            catch (err) {
                reportError(err, call);
            }
        }).then(() => {
            this.mLoadingCallbacks.forEach((cb) => {
                cb(undefined, allCalls.length);
            });
            log("debug", "once done");
        });
    }
    getProtocolHandler(protocol) {
        return this.mProtocolHandlers[protocol] || null;
    }
    get numOnce() {
        // Count both once and onceMain calls since we handle both in renderer
        const onceCalls = this.mContextProxyHandler.getCalls("once");
        const onceMainCalls = this.mContextProxyHandler.getCalls("onceMain");
        return onceCalls.length + onceMainCalls.length;
    }
    onLoadingExtension(cb) {
        this.mLoadingCallbacks.push(cb);
    }
    setUIReady() {
        this.mOnUIStarted();
    }
    watcherError = (err, selector) => {
        const id = selector.join(".");
        if (!this.mFailedWatchers.has(id)) {
            log("warn", "Failed to trigger state listener", {
                error: err.message,
                selector: JSON.stringify(selector),
            });
            this.mFailedWatchers.add(id);
        }
    };
    queryLoadTimeout(extension) {
        return PromiseBB.resolve(showMessageBox({
            type: "warning",
            title: "Extension slow",
            message: `An extension (${extension}) is taking unusually long to load. ` +
                "This is very likely a bug. Do you want to continue to wait for it?",
            noLink: true,
            buttons: ["Cancel", "Wait"],
        })).then((result) => result.response === 1);
    }
    getModDB = () => {
        const gameMode = activeGameId(this.mApi.store.getState());
        const currentKey = getSafe(this.mApi.store.getState(), ["confidential", "account", "nexus", "APIKey"], "");
        let init;
        let onDone;
        if (this.mModDBPromise === undefined) {
            this.mModDBPromise = new PromiseBB((resolve, reject) => {
                onDone = () => {
                    this.mModDBPromise = undefined;
                    resolve();
                };
            });
            init = PromiseBB.resolve();
        }
        else {
            init = this.mModDBPromise;
        }
        return init
            .then(() => {
            // reset the moddb if necessary so new settings get used
            if (this.mModDB === undefined ||
                this.mForceDBReconnect ||
                gameMode !== this.mModDBGame ||
                currentKey !== this.mModDBAPIKey) {
                this.mForceDBReconnect = false;
                if (this.mModDB !== undefined) {
                    return this.mModDB.close().then(() => (this.mModDB = undefined));
                }
            }
            return PromiseBB.resolve();
        })
            .then(() => this.mModDB !== undefined
            ? PromiseBB.resolve()
            : this.connectMetaDB(gameMode, currentKey).then((modDB) => {
                this.mModDB = modDB;
                this.mModDBGame = gameMode;
                this.mModDBAPIKey = currentKey;
                log("debug", "initialised");
            }))
            .then(() => this.mModDB)
            .finally(() => {
            if (onDone !== undefined) {
                onDone();
            }
        });
        // TODO: the fallback to nexus api should somehow be set up in nexus_integration, not here
    };
    canBeToast = (notif) => {
        const invalidToastTypes = ["activity", "warning"];
        if (notif.displayMS != null &&
            notif.displayMS <= 5000 &&
            notif.noToast !== true &&
            (notif.actions == null || notif.actions.length === 0) &&
            !invalidToastTypes.includes(notif.type)) {
            return true;
        }
        return false;
    };
    getMetaServerList() {
        const state = this.mApi.store.getState();
        const servers = getSafe(state, ["settings", "metaserver", "servers"], {});
        return Object.keys(servers)
            .map((id) => servers[id])
            .slice()
            .concat(Object.values(this.mProgrammaticMetaServers))
            .sort((lhs, rhs) => (lhs.priority ?? 100) - (rhs.priority ?? 100));
    }
    connectMetaDB(gameId, apiKey) {
        const dbPath = path.join(getVortexPath("userData"), "metadb");
        return modmeta.ModDB.create(dbPath, gameId, this.getMetaServerList(), log).catch((err) => {
            return this.mApi
                .showDialog("error", "Failed to connect meta database", {
                text: "Please check that there is no other instance of Vortex still running.",
                message: getErrorMessageOrDefault(err),
            }, [{ label: "Quit" }, { label: "Retry" }])
                .then((result) => {
                if (result.action === "Quit") {
                    getApplication().quit();
                    return PromiseBB.reject(new ProcessCanceled("meta db locked"));
                }
                return this.connectMetaDB(gameId, apiKey);
            });
        });
    }
    stateChangeHandler = (watchPath, callback, ext) => {
        if (!isFunction(callback)) {
            // TODO we should be throwing an exception here but this didn't fail in the past and I don't
            //   want to break previously ok extensions in a minor update
            // throw new Error('attempt to register invalid change handler');
            log("error", "attempt to register invalid change handler", {
                stack: new Error().stack,
            });
            return;
        }
        const stackErr = new Error();
        // have to initialize to a value that we _know_ is never set by the user.
        let lastValue = UNDEFINED;
        const key = watchPath.join(".");
        // TODO: this code makes using the ReduxWatcher pointless and looking at the
        //   code I would now disagree with the assessment that it may retrigger
        //   without an actual change. otoh I didn't add this for no reason...
        const changeHandler = ({ prevValue, currentValue }) => {
            // redux-watch may trigger even if no change occurred so we have to
            // do our own check, otherwise we could end up in an endless loop
            // if the callback causes redux-watch to trigger again without change
            if (currentValue === lastValue && lastValue !== UNDEFINED) {
                return;
            }
            lastValue = currentValue;
            this.mWatches[key].forEach((cb) => {
                try {
                    cb(prevValue, currentValue);
                }
                catch (unknownError) {
                    const err = unknownToError(unknownError);
                    log("error", "state change handler failed", {
                        message: err.message,
                        stack1: err.stack,
                        stack2: stackErr.stack,
                        key,
                    });
                }
            });
        };
        if (this.mWatches[key] === undefined) {
            this.mWatches[key] = [];
            this.mReduxWatcher.on(watchPath, changeHandler);
        }
        this.mWatches[key].push(wrapExtCBSync(callback, convertExtInfo(ext)));
    };
    showErrorBox = (message, details) => {
        const errMessage = getErrorMessageOrDefault(details);
        void showErrorBox(message, errMessage);
    };
    /**
     * initialize all extensions
     */
    initExtensions() {
        const context = {
            api: this.mApi,
        };
        this.mContextProxyHandler = new ContextProxyHandler(context);
        const contextProxy = new Proxy(context, this.mContextProxyHandler);
        this.mExtensions.forEach((ext) => {
            log("info", "init extension", { name: ext.name, path: ext.path });
            this.mContextProxyHandler.setExtension(ext.name, ext.path);
            try {
                const apiProxy = new APIProxyCreator(ext, this.mEventEmitter);
                const extProxy = new Proxy(contextProxy, apiProxy);
                const init = ext.initFunc();
                if (typeof init !== "function") {
                    const relevantInfo = _.pick(ext, ["name", "namespace", "path"]);
                    throw new Error(`corrupt extension, failed to initialize: ${JSON.stringify(relevantInfo)}`);
                }
                init(extProxy);
                apiProxy.enableAPI();
            }
            catch (unknownError) {
                if (!ext.dynamic) {
                    // if one of the static extension fails to initialize we should be
                    // crashing, otherwise we risk data loss if the user restores a backup
                    // and the important reducers aren't loaded
                    throw unknownError;
                }
                const err = unknownToError(unknownError);
                // make sure we're not calling any of the register calls if the extension
                // isn't fully initialized
                this.mContextProxyHandler.dropCalls(ext.name);
                this.mLoadFailures[ext.name] = [
                    { id: "exception", args: { message: err.message } },
                ];
                recordErrorSpan("Extension initialization failed", err);
                log("warn", "couldn't initialize extension", {
                    name: ext.name,
                    err: err.message,
                    stack: err.stack,
                });
            }
        });
        this.mContextProxyHandler.endRegistration();
        // need to store them locally for now because the store isn't loaded at this time
        this.mLoadFailures = {
            ...this.mLoadFailures,
            ...this.mContextProxyHandler.unloadIncompatible(ExtensionManager.sUIAPIs, this.mExtensions),
        };
        this.mOptionalExtensions = this.mContextProxyHandler.getOptionalExtensions(this.mExtensions);
        // apply api extensions immediately after all extensions are loaded so they
        // become available asap
        this.apply("registerAPI", (key, func, options) => {
            this.mApi.ext[key] = func;
        });
        log("info", "all extensions initialized");
    }
    migrateExtensions() {
        const migrations = {};
        this.mContextProxyHandler.getCalls("registerMigration").forEach((call) => {
            setdefault(migrations, call.extension, []).push(call.arguments[0]);
        });
        const state = this.mApi.store.getState();
        this.mExtensions
            .filter((ext) => ext.dynamic)
            .forEach((ext) => {
            try {
                let oldVersion = getSafe(state.app, ["extensions", ext.name, "version"], "0.0.0");
                if (!semver.valid(oldVersion)) {
                    log("error", "invalid version stored for extension", {
                        extension: ext.name,
                        oldVersion,
                    });
                    oldVersion = "0.0.0";
                }
                if (oldVersion !== ext.info.version) {
                    if (migrations[ext.name] === undefined) {
                        this.mApi.store.dispatch(setExtensionVersion(ext.name, ext.info.version));
                    }
                    else {
                        PromiseBB.mapSeries(migrations[ext.name], (mig) => mig(oldVersion))
                            .then(() => {
                            log("info", "set extension version", {
                                name: ext.name,
                                info: JSON.stringify(ext.info),
                            });
                            this.mApi.store.dispatch(setExtensionVersion(ext.name, ext.info.version));
                        })
                            .catch((err) => {
                            const error = unknownToError(err);
                            recordErrorSpan("Extension failed to migrate", error);
                            this.mApi.showErrorNotification("Extension failed to migrate", error, {
                                allowReport: ext.info.author === COMPANY_ID,
                            });
                        })
                            .then(() => null);
                    }
                }
            }
            catch (err) {
                const error = unknownToError(err);
                this.mApi.showErrorNotification("Extension invalid", error, {
                    allowReport: false,
                    message: ext.name,
                });
            }
        });
    }
    getPath(name) {
        return getVortexPath(name);
    }
    selectFile(options) {
        const fullOptions = {
            ..._.omit(options, ["create"]),
            properties: ["openFile"],
        };
        if (options.create === true) {
            fullOptions.properties.push("promptToCreate");
        }
        return PromiseBB.resolve(showOpenDialog(fullOptions)).then((result) => result.filePaths !== undefined && result.filePaths.length > 0
            ? result.filePaths[0]
            : undefined);
    }
    saveFile(options) {
        const fullOptions = {
            //..._.omit(options, ['create']),
            //properties: ['showOverwriteConfirmation'],
            ...options,
        };
        //if (options === true) {
        //fullOptions.properties.push('showOverwriteConfirmation');
        //}
        return PromiseBB.resolve(showSaveDialog(fullOptions)).then((result) => result.filePath !== undefined ? result.filePath : undefined);
    }
    selectExecutable(options) {
        // TODO: make the filter list dynamic based on the list of registered interpreters?
        const fullOptions = {
            ..._.omit(options, ["create"]),
            properties: ["openFile"],
            filters: [
                {
                    name: "All Executables",
                    extensions: ["exe", "cmd", "bat", "jar", "py"],
                },
                { name: "Native", extensions: ["exe", "cmd", "bat"] },
                { name: "Java", extensions: ["jar"] },
                { name: "Python", extensions: ["py"] },
            ],
        };
        return PromiseBB.resolve(showOpenDialog(fullOptions)).then((result) => result.filePaths !== undefined && result.filePaths.length > 0
            ? result.filePaths[0]
            : undefined);
    }
    selectDir(options) {
        const fullOptions = {
            ..._.omit(options, ["create"]),
            properties: ["openDirectory"],
        };
        return PromiseBB.resolve(showOpenDialog(fullOptions)).then((result) => result.filePaths !== undefined && result.filePaths.length > 0
            ? result.filePaths[0]
            : undefined);
    }
    commandLineUserData = () => this.mApi.getState().session.base.commandLine?.userData;
    registerProtocol = async (protocol, def, callback) => {
        log("info", "register protocol", { protocol });
        const haveToRegister = await registerProtocolHandler({
            protocol,
            setAsDefault: def,
            userDataPath: this.commandLineUserData(),
        });
        this.mProtocolHandlers[protocol] = callback;
        return haveToRegister;
    };
    registerRepositoryLookup = (repository, preferOverMD5, func) => {
        this.mRepositoryLookup[repository] = { preferOverMD5, func };
    };
    registerArchiveHandler = (extension, handler) => {
        this.mArchiveHandlers[extension] = handler;
    };
    deregisterProtocol = async (protocol) => {
        log("info", "deregister protocol");
        await deregisterProtocolHandler(protocol, this.commandLineUserData());
    };
    lookupModReference = (reference, options) => {
        if (options === undefined) {
            options = {};
        }
        // Spammy debug log
        // log('debug', 'lookup mod reference', { reference });
        let lookup;
        let preMD5 = PromiseBB.resolve([]);
        if (reference.repo !== undefined) {
            lookup = this.mRepositoryLookup[reference.repo.repository];
        }
        if (lookup !== undefined && lookup.preferOverMD5) {
            preMD5 = lookup.func(reference.repo);
        }
        return preMD5
            .then((results) => {
            if (options.requireURL === true) {
                results = results.filter((res) => truthy(res.value.sourceURI));
            }
            if (results.length !== 0) {
                return results;
            }
            else {
                return this.getModDB()
                    .then((modDB) => modDB.getByReference(reference))
                    .filter((mod) => {
                    if (options.requireURL === true) {
                        return truthy(mod.value.sourceURI);
                    }
                    else {
                        return true;
                    }
                })
                    .map((mod) => convertMD5Result(mod));
            }
        })
            .then((results) => {
            if (results.length !== 0) {
                if (reference.logicalFileName !== undefined) {
                    const exactMatch = results.filter((iter) => iter.value.logicalFileName !== undefined &&
                        iter.value.logicalFileName === reference.logicalFileName);
                    if (exactMatch.length > 0) {
                        return exactMatch;
                    }
                    else {
                        return results.sort((lhs, rhs) => fuzz.ratio(rhs.value.logicalFileName, reference.logicalFileName) -
                            fuzz.ratio(lhs.value.logicalFileName, reference.logicalFileName));
                    }
                }
                else {
                    return results;
                }
            }
            else {
                if (lookup !== undefined && !lookup.preferOverMD5) {
                    return lookup.func(reference.repo);
                }
                else {
                    return [];
                }
            }
        });
    };
    modLookupId(detail) {
        const san = (input) => path.basename(input, path.extname(input));
        const fileName = detail.filePath !== undefined
            ? san(detail.filePath)
            : detail.fileName !== undefined
                ? san(detail.fileName)
                : undefined;
        return (`${detail.fileMD5}_${fileName}` + `_${detail.fileSize}_${detail.gameId}`);
    }
    lookupModMeta = (detail, ignoreCache) => {
        if (detail.fileName !== undefined && detail.fileSize === 0) {
            log("error", "trying to calculate hash for an empty file", {
                name: detail.fileName,
                trace: new Error().stack,
            });
            const err = new ProcessCanceled("trying to calculate hash for an empty file");
            err["fileName"] = detail.fileName;
            return PromiseBB.reject(err);
        }
        if (detail.fileMD5 === undefined && detail.filePath === undefined) {
            return PromiseBB.resolve([]);
        }
        let lookupId = this.modLookupId(detail);
        if (this.mModDBCache[lookupId] !== undefined && ignoreCache !== true) {
            return PromiseBB.resolve(this.mModDBCache[lookupId]);
        }
        let fileMD5 = detail.fileMD5;
        let fileSize = detail.fileSize;
        if (fileMD5 === undefined && detail.filePath === undefined) {
            return PromiseBB.resolve([]);
        }
        let promise;
        if (fileMD5 === undefined) {
            promise = this.genMd5Hash(detail.filePath)
                .then((res) => {
                fileMD5 = res.md5sum;
                fileSize = res.numBytes;
                lookupId = this.modLookupId({
                    ...detail,
                    fileMD5,
                    fileSize,
                });
                this.getApi().events.emit("filehash-calculated", detail.filePath, fileMD5, fileSize, detail.gameId);
            })
                .catch((err) => {
                log("info", "failed to calculate hash", {
                    path: detail.filePath,
                    error: getErrorMessageOrDefault(err),
                });
                return PromiseBB.resolve();
            });
        }
        else {
            promise = PromiseBB.resolve();
        }
        // lookup id may be updated now
        if (this.mModDBCache[lookupId] !== undefined && ignoreCache !== true) {
            return PromiseBB.resolve(this.mModDBCache[lookupId]);
        }
        return promise
            .then(() => this.getModDB())
            .then((modDB) => fileSize !== 0 && fileMD5 !== undefined
            ? modDB.lookup(undefined, fileMD5, fileSize, detail.gameId)
            : [])
            .then((result) => {
            const resultSorter = this.makeSorter(detail);
            this.mModDBCache[lookupId] = result.sort(resultSorter);
            return PromiseBB.resolve(this.mModDBCache[lookupId]);
        });
    };
    makeSorter(detail) {
        const fileName = detail.filePath !== undefined
            ? path.basename(detail.filePath)
            : undefined;
        const hasAttribute = (attribute, lhs, rhs, preferredValue) => {
            if (lhs[attribute] === rhs[attribute]) {
                return 0;
            }
            if (preferredValue === undefined) {
                // if no preferred value was set, ensure it can never match
                preferredValue = Symbol();
            }
            if (!truthy(lhs[attribute]) || rhs[attribute] === preferredValue) {
                return 1;
            }
            else if (!truthy(rhs[attribute]) || lhs[attribute] === preferredValue) {
                return -1;
            }
            else {
                return 0;
            }
        };
        const numDetails = (result) => {
            return Object.keys(result.details || {}).length;
        };
        return (lhs, rhs) => {
            const lhsV = lhs.value;
            const rhsV = rhs.value;
            // prefer results where the file name matches, otherwise use the one with
            // more details
            return (hasAttribute("fileName", lhsV, rhsV, fileName) ||
                hasAttribute("source", lhsV, rhsV, "nexus") ||
                hasAttribute("sourceURI", lhsV, rhsV) ||
                hasAttribute("gameId", lhsV, rhsV) ||
                hasAttribute("fileVersion", lhsV, rhsV) ||
                hasAttribute("logicalFileName", lhsV, rhsV) ||
                numDetails(lhsV) - numDetails(rhsV));
        };
    }
    saveModMeta = (modInfo) => {
        const lookupId = this.modLookupId({
            fileMD5: modInfo.fileMD5,
            filePath: modInfo.fileName,
            fileSize: modInfo.fileSizeBytes,
            gameId: modInfo.gameId,
        });
        delete this.mModDBCache[lookupId];
        return this.getModDB().then((modDB) => {
            return new PromiseBB((resolve, reject) => {
                modDB.insert([modInfo]);
                resolve();
            });
        });
    };
    genMd5Hash = (data, progressFunc) => {
        let lastProgress = 0;
        const progressHash = progressFunc
            ? (progress, total) => {
                progressFunc(progress, total);
                lastProgress = total;
            }
            : undefined;
        return PromiseBB.resolve(fileMD5(data, progressHash)).then((md5sum) => {
            if (lastProgress > 0) {
                return { md5sum, numBytes: lastProgress };
            }
            const sizePromise = Buffer.isBuffer(data)
                ? PromiseBB.resolve(data.length)
                : fsVortex
                    .statAsync(data)
                    .then((stats) => stats.size)
                    .catch(() => 0);
            return sizePromise.then((numBytes) => ({ md5sum, numBytes }));
        });
    };
    openArchive = (archivePath, options, ext) => {
        if (this.mArchiveHandlers === undefined) {
            // lazy loading the archive handlers
            this.mArchiveHandlers = {};
            this.apply("registerArchiveType", this.registerArchiveHandler);
        }
        if (ext === undefined) {
            ext = path.extname(archivePath).substr(1);
        }
        const creator = this.mArchiveHandlers[ext];
        if (creator === undefined) {
            return PromiseBB.reject(new NotSupportedError());
        }
        return creator(archivePath, options || {}).then((handler) => PromiseBB.resolve(new Archive(handler)));
    };
    applyStartHooks(input) {
        let updated = input;
        return PromiseBB.each(this.mStartHooks, (hook) => hook
            .hook(updated)
            .then((newParameters) => {
            updated = newParameters;
        })
            .catch(UserCanceled, (err) => {
            log("debug", "start canceled by user");
            return PromiseBB.reject(err);
        })
            .catch(ProcessCanceled, (err) => {
            log("debug", "hook canceled start", getErrorMessageOrDefault(err));
            return PromiseBB.reject(err);
        })
            .catch((err) => {
            if (err instanceof UserCanceled) {
                log("debug", "start canceled by user");
            }
            else if (err instanceof ProcessCanceled) {
                log("debug", "hook canceled start", getErrorMessageOrDefault(err));
            }
            else {
                log("error", "hook failed", err);
            }
            return PromiseBB.reject(err);
        })).then(() => updated);
    }
    runExecutable = (executable, args, options) => {
        if (!truthy(executable)) {
            return PromiseBB.reject(new ProcessCanceled("Executable not set"));
        }
        const interpreter = this.mInterpreters[path.extname(executable).toLowerCase()];
        if (interpreter !== undefined) {
            try {
                ({ executable, args, options } = interpreter({
                    executable,
                    args,
                    options,
                }));
            }
            catch (err) {
                return PromiseBB.reject(err);
            }
        }
        const cwd = options.cwd || path.dirname(executable);
        // Detect if executable is a script file that requires shell: true
        // Common script extensions that need shell interpretation
        const scriptExtensions = [".ps1", ".bat", ".cmd", ".sh", ".bash"];
        const ext = path.extname(executable).toLowerCase();
        const isScript = scriptExtensions.includes(ext);
        // If it's a script and shell isn't explicitly set, enable shell mode
        if (isScript && options.shell === undefined) {
            options.shell = true;
        }
        // process.env is case insensitive (on windows at least?), but the spawn parameter isn't.
        // I think the key is called "Path" on windows but I'm not willing to bet this is consistent
        // across all language variants and versions
        const pathEnvName = Object.keys(process.env).find((key) => key.toLowerCase() === "path");
        const env = {
            ...filteredEnvironment(),
            [pathEnvName]: process.env["PATH_ORIG"] || process.env["PATH"],
            ...options.env,
        };
        // TODO: we might want to be much more restrictive in what keys we allow in environment variables,
        //   based on a quick google I could only find rules for Linux which appears to not allow the equal
        //   sign in keys either (which makes sense).
        //   On windows the empty string is the only thing I found that causes a problem though
        delete env[""];
        return (this.applyStartHooks({ executable, args, options })
            .then((updatedParameters) => {
            ({ executable, args, options } = updatedParameters);
            return PromiseBB.resolve();
        })
            .then(() => new PromiseBB((resolve, reject) => {
            const runExe = options.shell ? `"${executable}"` : executable;
            const spawnOptions = {
                cwd,
                env,
                detached: options.detach !== undefined ? options.detach : true,
                shell: options.shell ?? false,
            };
            try {
                const runParams = {
                    executable,
                    args,
                    options: { ...options, env },
                };
                const vars = this.mToolParameterCBs.reduce((prev, cb) => {
                    return { ...prev, ...cb(runParams) };
                }, {});
                args = args.map((arg) => applyVariables(arg, vars));
                const child = spawn(runExe, options.shell
                    ? args
                    : args.map((arg) => arg.replace(/"/g, "")), spawnOptions);
                if (truthy(child["exitCode"])) {
                    // brilliant, apparently there is no way for me to get at the stdout/stderr when running
                    // through a shell if starting the application fails immediately
                    return reject(new Error(`Failed to start (exit code ${child["exitCode"]})`));
                }
                if (options.onSpawned !== undefined) {
                    options.onSpawned(child.pid);
                }
                if (options.detach) {
                    child.unref();
                }
                let stdOut;
                let errOut;
                child
                    .on("error", (err) => {
                    reject(err);
                })
                    .on("close", (code, signal) => {
                    const game = activeGameId(this.mApi.store.getState());
                    if (code === null) {
                        log("warn", "child process terminated by signal", {
                            signal,
                        });
                        if (options.expectSuccess) {
                            const err = new ProcessCanceled(`Process terminated by signal ${signal ?? "unknown"}`);
                            err["signal"] = signal;
                            reject(err);
                            return;
                        }
                        resolve();
                        return;
                    }
                    if (game === "fallout3" && code === 0xc0000135) {
                        // 0xC0000135 means that a dll couldn't be found.
                        // In the context of FO3 it's commonly xlive or other redistribs are
                        //  not installed.
                        return reject(new MissingDependency());
                    }
                    else if (code === 0xe0434352) {
                        // A .net error, unfortunately we can't now if/how the actual exception
                        // text has been reported
                        log("warn", ".NET error", { stdOut, errOut });
                        if (game === "stardewvalley") {
                            // In the case of SDV the interesting information seems to get printed to stdout
                            return reject(new ThirdPartyError(stdOut || errOut));
                        }
                        else if (errOut) {
                            return reject(new ThirdPartyError(errOut));
                        }
                        else {
                            return reject(new ProcessCanceled(".NET error"));
                        }
                    }
                    else if (code === 0xc000026b) {
                        return reject(new ProcessCanceled("Windows shutting down"));
                    }
                    else if (code !== 0) {
                        // TODO: the child process returns an exit code of 53 for SSE and
                        // FO4, and an exit code of 1 for Skyrim. We don't know why but it
                        // doesn't seem to affect anything
                        log("warn", "child process exited with code: " + code.toString(16), {});
                        if (errOut !== undefined) {
                            log("warn", "child output", errOut.trim());
                        }
                        if (options.expectSuccess) {
                            let lastLine = "<No output>";
                            if (errOut !== undefined) {
                                const lines = errOut.trim().split("\n");
                                lastLine =
                                    lines.length > ERROR_OUTPUT_CUTOFF
                                        ? lines[lines.length - 1]
                                        : lines.join("\n");
                            }
                            // Sanitize the error message to prevent crashpad issues
                            const sanitizedExecutable = executable.replace(/[^\x20-\x7E]/g, "?");
                            const sanitizedLastLine = lastLine
                                .replace(/[^\x20-\x7E]/g, "?")
                                .substring(0, 500);
                            const exitCodeHex = code.toString(16);
                            const errorMessage = `Failed to run "${sanitizedExecutable}": "${sanitizedLastLine} (${exitCodeHex})"`;
                            const err = new Error(errorMessage);
                            err["exitCode"] = code;
                            reject(err);
                            return;
                        }
                    }
                    resolve();
                });
                if (child.stderr !== undefined) {
                    child.stderr.on("data", (chunk) => {
                        if (errOut === undefined) {
                            errOut = "";
                        }
                        try {
                            errOut += chunk.toString();
                        }
                        catch (err) {
                            log("warn", "error output from external process couldn't be processed", { executable });
                        }
                    });
                }
                if (child.stdout !== undefined) {
                    child.stdout.on("data", (chunk) => {
                        if (stdOut === undefined) {
                            stdOut = "";
                        }
                        try {
                            stdOut += chunk.toString();
                        }
                        catch (err) {
                            log("warn", "output from external process couldn't be processed", { executable });
                        }
                    });
                }
            }
            catch (err) {
                const code = getErrorCode(err);
                if (code === "EINVAL") {
                    err["attachLogOnReport"] = true;
                    log("error", "Invalid spawn parameters", {
                        runExe,
                        args,
                        options: JSON.stringify(options),
                    });
                }
                return reject(err);
            }
        }))
            .catch(ProcessCanceled, () => null)
            .catch({ code: "EACCES" }, (err) => {
            // Elevated execution is only supported on Windows
            if (process.platform !== "win32") {
                return PromiseBB.reject(err);
            }
            return this.runElevated(executable, cwd, args, env, options.onSpawned);
        })
            .catch({ code: "ECANCELED" }, () => PromiseBB.reject(new UserCanceled()))
            .catch({ systemCode: 1223 }, () => PromiseBB.reject(new UserCanceled()))
            // Is errno still used ? looks like shellEx call returns systemCode instead
            .catch({ errno: 1223 }, () => PromiseBB.reject(new UserCanceled()))
            .catch((unknownErr) => {
            const err = unknownToError(unknownErr);
            if (err.message
                .toLowerCase()
                .indexOf("the operation was canceled by the user") !== -1) {
                // This is more of a sanity check than anything else as one user report
                //  contained none of the properties we rely on to detect when a user
                //  cancels the UAC dialog.
                //  https://github.com/Nexus-Mods/Vortex/issues/8524
                return PromiseBB.reject(new UserCanceled());
            }
            return PromiseBB.reject(err);
        }));
    };
    runElevated(executable, cwd, args, env, onSpawned) {
        const ipcPath = shortid();
        let tmpFilePath;
        return new PromiseBB((resolve, reject) => {
            this.startIPC(ipcPath, (err) => {
                if (err !== null) {
                    reject(err);
                }
                else {
                    resolve();
                }
            });
            log("debug", "running elevated", { executable, cwd, args });
            runElevated(ipcPath, runElevatedCustomTool, {
                toolPath: executable,
                toolCWD: cwd,
                parameters: args,
                environment: env,
            })
                .then((tmpPath) => {
                tmpFilePath = tmpPath;
                if (onSpawned !== undefined) {
                    onSpawned();
                }
            })
                .catch((err) => reject(err));
        }).finally(() => {
            if (tmpFilePath !== undefined) {
                try {
                    fs.unlinkSync(tmpFilePath);
                }
                catch (err) {
                    // nop
                }
            }
        });
    }
    emitAndAwait = (event, ...args) => {
        let queue = PromiseBB.resolve();
        const results = [];
        const enqueue = (prom) => {
            if (prom !== undefined) {
                queue = queue.then(() => prom
                    .then((res) => {
                    if (res !== undefined && res !== null) {
                        results.push(res);
                    }
                })
                    .catch((err) => {
                    this.mApi.showErrorNotification(`Unhandled error in event "${event}"`, err);
                }));
            }
        };
        this.mEventEmitter.emit(event, ...args, enqueue);
        return queue.then(() => results);
    };
    onAsync = (event, listener, extInfo) => {
        const effectiveListener = wrapExtCBAsync(listener, extInfo);
        this.mEventEmitter.on(event, (...args) => {
            const enqueue = args.pop();
            if (enqueue === undefined || typeof enqueue !== "function") {
                // no arguments, this is not an emitAndAwait event!
                this.mApi.showErrorNotification("Invalid event handler", { event });
                if (enqueue !== undefined) {
                    args.push(enqueue);
                }
                // call the listener anyway
                const prom = effectiveListener(...args);
                if (prom["catch"] !== undefined) {
                    prom["catch"]((err) => {
                        this.mApi.showErrorNotification(`Failed to call event ${event}`, err);
                    });
                }
            }
            else {
                enqueue(effectiveListener(...args));
            }
        });
    };
    withPrePost = (eventName, cb) => {
        return (...args) => {
            return this.emitAndAwait(`will-${eventName}`, ...args)
                .then(() => cb(...args))
                .then((res) => this.emitAndAwait(`did-${eventName}`, res, ...args).then(() => res));
        };
    };
    // tslint:disable-next-line:member-ordering
    highlightCSS = (() => {
        let highlightCSS;
        let highlightCSSAlt;
        let highlightAfterCSS;
        let highlightBeforeCSSAlt;
        const initCSS = () => {
            if (highlightCSS !== undefined) {
                return;
            }
            highlightCSS = highlightAfterCSS = null;
            // tslint:disable-next-line:prefer-for-of
            for (let i = 0; i < document.styleSheets.length; ++i) {
                if (document.styleSheets[i].ownerNode.id === "theme") {
                    const rules = Array.from(document.styleSheets[i].rules);
                    rules.forEach((rule) => {
                        if (rule.selectorText === "#highlight-control-dummy") {
                            highlightCSS = rule;
                        }
                        else if (rule.selectorText === "#highlight-control-dummy-alt") {
                            highlightCSSAlt = rule;
                        }
                        else if (rule.selectorText === "#highlight-control-dummy::after") {
                            highlightAfterCSS = rule;
                        }
                        else if (rule.selectorText === "#highlight-control-dummy-alt::before") {
                            highlightBeforeCSSAlt = rule;
                        }
                    });
                }
            }
        };
        return (selector, text, altStyle) => {
            initCSS();
            let result = "";
            const css = altStyle ? highlightCSSAlt : highlightCSS;
            const afterCSS = highlightAfterCSS;
            const dummySelector = altStyle
                ? "#highlight-control-dummy-alt"
                : "#highlight-control-dummy";
            // adding a new css rule matching the selector when we could just as well add
            // the highlight class to the control.
            // The reason it's done this way is because it's less messy (easier to clean up one css
            // rule instead of every control matched by the selector) and it doesn't interfere with
            // react, which might re-generate every control.
            if (highlightCSS === null) {
                // fallback if template rules weren't found
                result += `${selector} { border: 1px solid var(--brand-danger) !important }\n`;
                if (text !== undefined) {
                    result += `${selector}::after { color: var(--brand-danger); content: "${text}" }\n`;
                }
            }
            else {
                result += css.cssText.replace(dummySelector, selector);
                if (altStyle) {
                    result += highlightBeforeCSSAlt.cssText.replace(dummySelector, selector);
                }
                if (text !== undefined) {
                    result += afterCSS.cssText
                        .replace("#highlight-control-dummy", selector)
                        .replace("__contentPlaceholder", text);
                }
            }
            return result;
        };
    })();
    highlightControl = (selector, duration, text, altStyle) => {
        const id = shortid();
        const style = document.createElement("style");
        style.id = `highlight_${id}`;
        style.type = "text/css";
        style.innerHTML = this.highlightCSS(selector, text, altStyle);
        const head = document.getElementsByTagName("head")[0];
        const highlightNode = head.appendChild(style);
        setTimeout(() => {
            head.removeChild(highlightNode);
        }, duration);
    };
    addMetaServer = (id, server) => {
        if (server !== undefined) {
            this.mProgrammaticMetaServers[id] = server;
        }
        else {
            delete this.mProgrammaticMetaServers[id];
        }
        this.mForceDBReconnect = true;
    };
    startIPC(ipcPath, onFinished) {
        let connected = false;
        const finish = (err) => {
            server.close();
            onFinished(err);
        };
        const server = net
            .createServer((connRaw) => {
            const conn = new JsonSocket(connRaw);
            log("debug", "ipc client connected");
            connected = true;
            conn
                .on("message", (data) => {
                const { message, payload } = data;
                if (message === "log") {
                    // tslint:disable-next-line:no-shadowed-variable
                    const { level, message, meta } = payload;
                    log(level, message, meta);
                }
                else if (message === "finished") {
                    finish(null);
                }
            })
                .on("error", (err) => {
                log("error", "elevated code reported error", err);
                finish(err);
            });
        })
            .listen(path.join("\\\\?\\pipe", ipcPath));
    }
    idify(name, pathName) {
        const transform = (input) => input.toLowerCase().replace(/[:']/g, "").replace(/[ _]/g, "-").trim();
        if (name !== undefined) {
            return transform(name);
        }
        else {
            // assuming the path is based on a nexus archive name, there should be a
            // -<modid>- tag after the actual mod name
            return pathName.split(/-\w+-/)[0];
        }
    }
    loadDynamicExtension(extensionPath, alreadyLoaded, bundled) {
        const indexPath = this.mExtensionFormats
            .map((format) => path.join(extensionPath, format))
            .find((iter) => fs.existsSync(iter));
        if (indexPath !== undefined) {
            let info = {
                name: "",
                author: "",
                description: "",
                version: "",
            };
            try {
                info = JSON.parse(fs.readFileSync(path.join(extensionPath, "info.json"), {
                    encoding: "utf8",
                }));
            }
            catch (err) {
                const errorCode = getErrorCode(err);
                const errMessage = errorCode === "ENOENT"
                    ? "extension has no info.json file"
                    : "failed to parse info.json file";
                log("warn", errMessage, {
                    extensionPath,
                    error: getErrorMessageOrDefault(err),
                });
            }
            const pathName = path.basename(extensionPath);
            const name = info.id || pathName;
            const namespace = info.namespace ??
                info.id ??
                (bundled ? pathName : this.idify(info.name, pathName));
            const existing = alreadyLoaded.find((reg) => reg.name === name);
            if (existing) {
                if (semver.gte(info.version, existing.info.version)) {
                    this.mOutdated.push(path.basename(existing.path));
                }
                return undefined;
            }
            return {
                name,
                namespace,
                initFunc: () => ExtensionManager.loadExternalExtension(indexPath),
                path: extensionPath,
                dynamic: true,
                info: {
                    ...info,
                    bundled,
                },
            };
        }
        else {
            // this is not necessarily a problem, translation extensions for example
            // have no index.js file
            log("debug", "extension directory contains no index.js file", {
                extensionPath,
            });
            return undefined;
        }
    }
    static loadExternalExtension(id) {
        // NOTE(erri120): Hack for dynamically importing extensions.
        // Webpack normally rewrites all requires to a custom __webpack__require
        // but here we don't want that. We want the raw "normal" require.
        const mod = webpackRequireHack(id);
        return this.getExtensionInitFunc(mod);
    }
    /** Finds the default exported extension init function of a module */
    static getExtensionInitFunc(mod) {
        if (!mod)
            return undefined;
        if (typeof mod === "function")
            return mod;
        if (typeof mod !== "object")
            return undefined;
        // NOTE(erri120): interop hacks because of jank...
        const fn = mod["__esModule"]
            ? mod["default"]
            : (mod["default"] ?? mod);
        return typeof fn === "function" ? fn : undefined;
    }
    loadDynamicExtensions(extension, loadedExtensions, alreadyLoaded) {
        if (!fs.existsSync(extension.path)) {
            log("info", "failed to load dynamic extensions, path doesn't exist", extension.path);
            try {
                fs.mkdirSync(extension.path);
            }
            catch (err) {
                log("warn", "extension path missing and can't be created", {
                    path: extension.path,
                    error: getErrorMessageOrDefault(err),
                });
            }
            return [];
        }
        const res = fs
            .readdirSync(extension.path)
            .filter((name) => fs.statSync(path.join(extension.path, name)).isDirectory())
            .reduce((prev, name) => {
            if (!getSafe(this.mExtensionState, [name, "enabled"], true)) {
                log("debug", "extension disabled", { name });
                return prev;
            }
            try {
                // first, mark this extension as loaded. If this is a user extension and there is an
                // extension with the same name in the bundle we could otherwise end up loading the
                // bundled one if this one fails to load which could be convenient but also massively
                // confusing.
                const before = Date.now();
                const ext = this.loadDynamicExtension(path.join(extension.path, name), alreadyLoaded, extension.bundled);
                if (ext !== undefined) {
                    if (this.mExtensionState?.[ext.name]?.enabled === false) {
                        log("debug", "extension disabled", { name: ext.name });
                        return prev;
                    }
                    loadedExtensions.add(ext.name);
                    const loadTime = Date.now() - before;
                    log("debug", "loaded extension", {
                        name,
                        loadTime,
                        location: extension.path,
                    });
                    if (prev[ext.name] !== undefined) {
                        // loadDynamicExtension already handles the case where the same extension was found
                        // in a different directory, but if the same directory contains multiple copies
                        // of the same extension, we have to deal with that slightly differently
                        log("warn", "multiple copies of the same extension installed", {
                            first: ext.path,
                            second: prev[ext.name].path,
                        });
                        if (ext.info === undefined ||
                            semver.gt(prev[ext.name].info?.version, ext.info?.version)) {
                            // the copy we loaded previously is newer so mark this one for removal and not
                            // load it
                            this.mOutdated.push(path.basename(ext.path));
                        }
                        else {
                            // this copy is actually the newer one so replace the one previously found and
                            // mark that for deletion
                            this.mOutdated.push(path.basename(prev[ext.name].path));
                            prev[ext.name] = ext;
                        }
                    }
                    else {
                        prev[ext.name] = ext;
                    }
                }
            }
            catch (unknownError) {
                const err = unknownToError(unknownError);
                log("warn", "failed to load dynamic extension", {
                    name,
                    error: err.message,
                    stack: err.stack,
                });
                this.mLoadFailures[name] = [
                    { id: "exception", args: { message: err.message } },
                ];
            }
            return prev;
        }, {});
        return Object.values(res);
    }
    /**
     * retrieves all extensions to the base functionality, both the static
     * and external ones.
     * This loads external extensions from disc synchronously
     */
    prepareExtensions() {
        const staticExtensions = {
            about_dialog: () => require("./extensions/about_dialog/index.ts"),
            adaptor_bridge: () => require("./extensions/adaptor_bridge/index.ts"),
            analytics: () => require("./extensions/analytics/index.ts"),
            announcement_dashlet: () => require("./extensions/announcement_dashlet/index.ts"),
            browse_nexus: () => require("./extensions/browse_nexus/index.ts"),
            browser: () => require("./extensions/browser/index.ts"),
            category_management: () => require("./extensions/category_management/index.ts"),
            collections_integration: () => require("./extensions/collections_integration/index.ts"),
            dashboard: () => require("./extensions/dashboard/index.ts"),
            design_system_dev: () => require("./extensions/design_system_dev/index.ts"),
            diagnostics_files: () => require("./extensions/diagnostics_files/index.ts"),
            download_management: () => require("./extensions/download_management/index.ts"),
            extension_manager: () => require("./extensions/extension_manager/index.ts"),
            file_based_loadorder: () => require("./extensions/file_based_loadorder/index.ts"),
            file_preview: () => require("./extensions/file_preview/index.ts"),
            firststeps_dashlet: () => require("./extensions/firststeps_dashlet/index.ts"),
            // gameversion_management must be listed before gamemode_management so that
            // GameVersionManager is initialized before gamemode_management's once()
            // calls setupGameMode -> getInstalledVersion
            gameversion_management: () => require("./extensions/gameversion_management/index.ts"),
            gamemode_management: () => require("./extensions/gamemode_management/index.ts"),
            hardlink_activator: () => require("./extensions/hardlink_activator/index.ts"),
            health_check: () => require("./extensions/health_check/index.ts"),
            history_management: () => require("./extensions/history_management/index.ts"),
            ini_prep: () => require("./extensions/ini_prep/index.ts"),
            installer_dotnet: () => require("./extensions/installer_dotnet/index.ts"),
            installer_fomod_ipc: () => require("./extensions/installer_fomod_ipc/index.ts"),
            installer_fomod_native: () => require("./extensions/installer_fomod_native/index.ts"),
            installer_fomod_shared: () => require("./extensions/installer_fomod_shared/index.ts"),
            installer_nested_fomod: () => require("./extensions/installer_nested_fomod/index.ts"),
            instructions_overlay: () => require("./extensions/instructions_overlay/index.ts"),
            mod_load_order: () => require("./extensions/mod_load_order/index.ts"),
            mod_management: () => require("./extensions/mod_management/index.ts"),
            mod_spotlights_dashlet: () => require("./extensions/mod_spotlights_dashlet/index.ts"),
            move_activator: () => require("./extensions/move_activator/index.ts"),
            news_dashlet: () => require("./extensions/news_dashlet/index.ts"),
            nexus_integration: () => require("./extensions/nexus_integration/index.tsx"),
            null_activator: () => require("./extensions/null_activator/index.ts"),
            onboarding_dashlet: () => require("./extensions/onboarding_dashlet/index.ts"),
            profile_management: () => require("./extensions/profile_management/index.ts"),
            recovery: () => require("./extensions/recovery/index.ts"),
            settings_application: () => require("./extensions/settings_application/index.ts"),
            settings_interface: () => require("./extensions/settings_interface/index.ts"),
            settings_metaserver: () => require("./extensions/settings_metaserver/index.ts"),
            starter_dashlet: () => require("./extensions/starter_dashlet/index.ts"),
            sticky_mods: () => require("./extensions/sticky_mods/index.ts"),
            symlink_activator: () => require("./extensions/symlink_activator/index.ts"),
            symlink_activator_elevate: () => require("./extensions/symlink_activator_elevate/index.ts"),
            test_runner: () => require("./extensions/test_runner/index.ts"),
            tool_variables_base: () => require("./extensions/tool_variables_base/index.ts"),
            updater: () => require("./extensions/updater/index.ts"),
        };
        require("./util/extensionRequire").default(() => this.extensions);
        const extensionPaths = ExtensionManager.getExtensionPaths();
        const loadedExtensions = new Set();
        let dynamicallyLoaded = [];
        return Object.keys(staticExtensions)
            .map((name) => ({
            name,
            namespace: name,
            path: path.resolve(__dirname, "extensions", name),
            initFunc: () => {
                const f = staticExtensions[name];
                return ExtensionManager.getExtensionInitFunc(f());
            },
            dynamic: false,
        }))
            .concat(...extensionPaths.map((extSpec) => {
            const newExtensions = this.loadDynamicExtensions(extSpec, loadedExtensions, dynamicallyLoaded);
            dynamicallyLoaded = dynamicallyLoaded.concat(newExtensions);
            return newExtensions;
        }));
    }
}
export default ExtensionManager;
